import React, { useEffect, useState } from "react";

export const STATUS_LIVE = new Set(["1H", "2H", "ET", "BT", "INP", "P", "LIVE", "HT"]);
export const STATUS_DONE = new Set(["FT", "AET", "PEN", "FT_PEN", "AWD", "WO"]);

const useCountdown = (iso) => {
  const [, tick] = useState(0);
  useEffect(() => {
    const id = setInterval(() => tick((t) => t + 1), 1000);
    return () => clearInterval(id);
  }, []);
  if (!iso) return null;
  const diff = new Date(iso).getTime() - Date.now();
  if (diff <= 0) return null;
  const totalSec = Math.floor(diff / 1000);
  const days = Math.floor(totalSec / 86400);
  const hours = Math.floor((totalSec % 86400) / 3600);
  const mins = Math.floor((totalSec % 3600) / 60);
  const secs = totalSec % 60;
  return { days, hours, mins, secs };
};

/**
 * StatusMeter — fixture status pill.
 * variant: "header" | "centerpiece" | "compact"
 */
export const StatusMeter = ({ fixture, variant = "header" }) => {
  const short = (fixture?.status?.short || "").toUpperCase();
  const elapsed = fixture?.status?.elapsed;
  const countdown = useCountdown(fixture?.date);

  const cls =
    variant === "centerpiece"
      ? "ff-centerpiece"
      : variant === "compact"
      ? "ff-compact-status"
      : "ff-status";

  if (STATUS_LIVE.has(short)) {
    if (variant === "compact") {
      return (
        <div className={`${cls} ${cls}-live`}>
          <span className="font-extrabold">{short || "LIVE"}</span>
        </div>
      );
    }
    return (
      <div className={`${cls} ${cls}-live`}>
        <span className="ff-pulse" />
        <span className="font-extrabold">LIVE</span>
        {elapsed != null && <span className="opacity-80">· {elapsed}'</span>}
      </div>
    );
  }
  if (STATUS_DONE.has(short)) {
    const homeGoals = fixture?.goals?.home;
    const awayGoals = fixture?.goals?.away;
    if (variant === "compact") {
      const score =
        homeGoals != null || awayGoals != null
          ? `${homeGoals ?? 0}–${awayGoals ?? 0}`
          : null;
      return (
        <div className={`${cls} ${cls}-done`}>
          <span className="font-extrabold">
            {short || "FT"}
            {score != null && <> {score}</>}
          </span>
        </div>
      );
    }
    return (
      <div className={`${cls} ${cls}-done`}>
        <span>FT</span>
        {(homeGoals != null || awayGoals != null) && (
          <span className="font-extrabold tabular-nums">
            {homeGoals ?? 0}–{awayGoals ?? 0}
          </span>
        )}
      </div>
    );
  }

  /* Compact (pulse rows): status code only — never kickoff countdown */
  if (variant === "compact") {
    const label = short || "NS";
    return (
      <div className={`${cls} ${cls}-soon`}>
        <span className="font-extrabold">{label}</span>
      </div>
    );
  }

  if (countdown) {
    const { days, hours, mins, secs } = countdown;
    const compact =
      days > 0
        ? `${days}d ${String(hours).padStart(2, "0")}h`
        : hours > 0
        ? `${String(hours).padStart(2, "0")}:${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`
        : `${String(mins).padStart(2, "0")}:${String(secs).padStart(2, "0")}`;
    return (
      <div className={`${cls} ${cls}-soon`}>
        {variant !== "compact" && (
          <span className="text-[9px] font-bold uppercase tracking-[0.18em] opacity-70">
            Kickoff
          </span>
        )}
        <span className="tabular-nums font-extrabold">{compact}</span>
      </div>
    );
  }
  return (
    <div className={`${cls} ${cls}-soon`}>
      <span className="text-[10px] font-bold uppercase tracking-[0.18em]">Scheduled</span>
    </div>
  );
};

export const TeamLogo = ({ team, size = "md" }) => {
  const initial = (team?.name?.[0] || "?").toUpperCase();
  const [failed, setFailed] = useState(false);
  const logo =
    typeof team?.logo === "string" && team.logo.trim() ? team.logo.trim() : null;

  useEffect(() => {
    setFailed(false);
  }, [logo]);

  return (
    <span className={`ff-team-logo ${size === "sm" ? "ff-team-logo-sm" : ""}`}>
      {logo && !failed ? (
        // eslint-disable-next-line jsx-a11y/alt-text
        <img
          src={logo}
          loading="lazy"
          referrerPolicy="no-referrer"
          onError={() => setFailed(true)}
        />
      ) : (
        <span>{initial}</span>
      )}
    </span>
  );
};
